import React, { useState, useEffect } from 'react';
import { DailyReportCard } from './DailyReportCard';
import { api } from '../lib/api';

interface DailyReportsProps {
  mundusId: string;
}

export const DailyReports: React.FC<DailyReportsProps> = ({ mundusId }) => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/reports/mundus/${mundusId}?limit=10`);
      setReports(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching reports:', err);
      setError(err.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    try {
      setGenerating(true);
      setError(null);
      await api.post(`/api/reports/mundus/${mundusId}/generate`);
      // Refresh the reports list
      await fetchReports();
    } catch (err: any) {
      console.error('Error generating report:', err);
      setError(err.response?.data?.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const markAsRead = async (reportId: string) => {
    try {
      await api.post(`/api/reports/${reportId}/read`);
      // Update the local state
      setReports(
        reports.map((report) =>
          report.id === reportId
            ? { ...report, isRead: true, readAt: new Date().toISOString() }
            : report
        )
      );
    } catch (err: any) {
      console.error('Error marking report as read:', err);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [mundusId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="cyber-spinner w-12 h-12"></div>
        <p className="ml-4 text-sm text-cyber-text-secondary font-mono animate-pulse">
          &gt; LOADING REPORTS...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-cyber-border">
        <div>
          <h2 className="text-2xl font-display font-bold text-cyber-text-primary uppercase tracking-wider">
            <span className="neon-text">DAILY</span> REPORTS
          </h2>
          <p className="text-xs text-cyber-text-secondary font-mono mt-1">
            &gt; AI-Generated Performance Analysis
          </p>
        </div>
        <button
          onClick={generateReport}
          disabled={generating}
          className="cyber-button disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {generating ? (
            <>
              <div className="cyber-spinner w-4 h-4"></div>
              <span>GENERATING...</span>
            </>
          ) : (
            <>
              <span>📊</span>
              <span>GENERATE REPORT</span>
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-cyber-error/10 border border-cyber-error text-cyber-error px-4 py-3 rounded-lg animate-slide-in">
          <div className="flex items-start space-x-2">
            <span className="text-lg">⚠️</span>
            <p className="font-mono flex-1">&gt; ERROR: {error}</p>
          </div>
        </div>
      )}

      {/* Reports List */}
      {reports.length === 0 ? (
        <div className="cyber-card text-center py-16 scanline">
          <div className="text-6xl mb-6 animate-float">📊</div>
          <h3 className="text-xl font-display font-bold text-cyber-text-primary mb-3 uppercase">
            NO REPORTS <span className="neon-text">DETECTED</span>
          </h3>
          <p className="text-cyber-text-secondary max-w-md mx-auto font-mono text-sm mb-8">
            &gt; Initialize AI-powered performance analysis to track your progress and receive insights
          </p>
          <button
            onClick={generateReport}
            disabled={generating}
            className="cyber-button disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-2"
          >
            {generating ? (
              <>
                <div className="cyber-spinner w-4 h-4"></div>
                <span>GENERATING...</span>
              </>
            ) : (
              <span>GENERATE FIRST REPORT</span>
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <DailyReportCard key={report.id} report={report} onMarkAsRead={markAsRead} />
          ))}
        </div>
      )}
    </div>
  );
};
